import { useImperativeHandle, ReactElement, ReactNode, MutableRefObject } from 'react';
import { App, Button, Flex, Form, FormInstance, FormItemProps, FormProps, Modal, ModalProps, Spin, Image } from 'antd';
import { useTranslation } from 'react-i18next';
import { FormWrap, ModalTitleWrap } from './style';
import { DeleteOutlined, CloseOutlined } from '@ant-design/icons';
import { SConModal } from '../SConModal';

type ModelFormRef<FormDetail> = FormInstance<FormDetail> | null;

interface SFormProps<FormDetail> extends ModalProps {
	handleCancel: () => void;
	handleOk: (data: FormDetail) => Promise<void>;
	handleDel?: (data: FormDetail) => Promise<void>;
	data: FormDetail | null; // 详情
	leftTitle: ReactNode;
	formItems?: Array<FormItemProps<FormDetail>>;
	modelFormRef: MutableRefObject<ModelFormRef<FormDetail>>;
	delContentTip?: string;
	formProps?: FormProps;
	loading?: boolean;
	spinning?:boolean;
	picUrl?: string;
	showPic?: boolean | undefined;
	setPicUrl?:() => void;
}

const SModalForm = <FormDetail,>({
	modelFormRef,
	formProps,
	loading,
	...props
}: SFormProps<FormDetail>): ReactElement => {
	const {
		handleCancel,
		handleOk,
		handleDel,
		data,
		leftTitle,
		formItems,
		delContentTip,
		spinning,
		picUrl,
		showPic,
		setPicUrl,
		...modalProps
	} = props;
	const { t } = useTranslation();
	const { notification } = App.useApp();
	const [modalForm] = Form.useForm<FormDetail>();

	useImperativeHandle(modelFormRef, () => modalForm);

	const onOk = () => {
		return modalForm
			.validateFields()
			.then((res) => {
				return handleOk(res)
					.then(() => {
						modalForm.resetFields();
					})
					.catch(() => undefined);
			})
			.catch((err) => {
				const errTitle = t('common.addError');
				const errorDesc = err?.errorFields[0]?.errors[0] ?? '';
				notification.error({ placement: 'top',
					message: errTitle,
					description: errorDesc,
				});
			});
	};

	const onClear = () => {
		modalForm.resetFields();
		setPicUrl && setPicUrl()
		handleCancel();
	};

	return (
		<Modal
			width={800}
			onOk={onOk}
			onCancel={onClear}
			closable={false}
			maskClosable={false}
			centered
			title={
				<ModalTitleWrap justify="space-between" align="center">
					{leftTitle}
					<div className="right-action--wrap">
						{data && handleDel && (
							<SConModal
								title={t('common.deleteTip')}
								content={delContentTip ?? ''}
								onOk={() => handleDel(data)}
								children={<Button icon={<DeleteOutlined />} danger type="text" />}
							/>
						)}
						<Button onClick={onClear} icon={<CloseOutlined />} type="text" />
					</div>
				</ModalTitleWrap>
			}
			okButtonProps={{ disabled: props.confirmLoading }}
			cancelButtonProps={{ disabled: props.confirmLoading }}
			{...modalProps}
		>
			{loading ? <Flex style={{ width: '100%', height: '100%' }} justify='center' align='center'><Spin /></Flex> : <FormWrap>
				<Form<FormDetail>
					form={modalForm}
					layout="horizontal"
					labelWrap
					autoComplete="off"
					initialValues={data ?? {}}
					{...formProps}
				>
					
					{ showPic && <div style={{ position: 'absolute', right: 54, top: 0, zIndex: 2 }}>
						<Spin spinning={spinning}>
							<Image
							style={{ width: 120, height: 120 }}
							src={picUrl}
							fallback="data:image/png;base64,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"
							/>
						</Spin>
					</div>
					}
					{formItems?.map((item, index) => <Form.Item key={index} {...item} />)}
				</Form>
			</FormWrap>}
		</Modal>
	);
};

export default SModalForm;
